<template>
  <div class="disputed-orders__table-wrapper">
    <div class="table-container">
      <table class="modern-table">
        <thead>
          <tr>
            <th @click="handleSort('invoice_id')" class="sortable">
              Invoice #
              <span class="sort-indicator">{{ getSortIcon('invoice_id') }}</span>
            </th>
            <th @click="handleSort('customer_name')" class="sortable">
              Customer
              <span class="sort-indicator">{{ getSortIcon('customer_name') }}</span>
            </th>
            <th @click="handleSort('dispute_type')" class="sortable">
              Dispute Type
              <span class="sort-indicator">{{ getSortIcon('dispute_type') }}</span>
            </th>
            <th>Finance Company</th>
            <th>Statement</th>
            <th>Issue Type</th>
            <th>Resolution</th>
            <th @click="handleSort('resolution_outcome')" class="sortable">
              Outcome
              <span class="sort-indicator">{{ getSortIcon('resolution_outcome') }}</span>
            </th>
            <th>Money Lost</th>
            <th>Amount</th>
            <th @click="handleSort('created_at')" class="sortable">
              Created
              <span class="sort-indicator">{{ getSortIcon('created_at') }}</span>
            </th>
            <th class="actions-header">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(dispute, index) in disputes" :key="dispute.id">
            <td>
              <span class="invoice-id">{{ dispute.invoice_id }}</span>
              <span class="status-tag" :class="dispute.status === 'resolved' ? 'resolved' : 'open'">
                {{ dispute.status === 'resolved' ? 'Resolved' : 'Open' }}
              </span>
            </td>
            <td>
              <div class="customer-cell">
                <div class="customer-name">{{ getCustomerName(dispute) }}</div>
                <div class="customer-detail">{{ getCustomerPhone(dispute) }}</div>
                <div class="customer-detail">{{ getCustomerEmail(dispute) }}</div>
              </div>
            </td>
            <td>{{ formatDisputeType(dispute.dispute_type) }}</td>
            <td>{{ dispute.dispute_company_name || dispute.finance_company_name || '-' }}</td>
            <td>
              <span class="statement-status" :class="dispute.customer_statement === 'received' ? 'received' : 'not-received'">
                {{ dispute.customer_statement === 'received' ? 'Received' : 'Not Received' }}
              </span>
            </td>
            <td>{{ formatIssueType(dispute.issue_type) }}</td>
            <td>
              <span v-if="dispute.resolution && dispute.resolution !== 'pending'" class="resolution-value">
                {{ formatResolution(dispute.resolution) }}
              </span>
              <span v-else class="pending-value">Pending</span>
            </td>
            <td>
              <span v-if="dispute.resolution && dispute.resolution !== 'pending'" class="outcome-value" :class="getResolutionOutcome(dispute.resolution) === 'Customer Favor' ? 'customer' : 'business'">
                {{ getResolutionOutcome(dispute.resolution) }}
              </span>
              <span v-else class="pending-value">—</span>
            </td>
            <td>
              <span class="money-status" :class="dispute.money_lost ? 'lost' : 'none'">
                {{ dispute.money_lost ? 'Yes' : 'No' }}
              </span>
            </td>
            <td>
              <span v-if="dispute.money_lost && dispute.money_amount" class="amount-value">
                {{ formatMoney(dispute.money_amount) }}
              </span>
              <span v-else class="empty-value">—</span>
            </td>
            <td>
              <div class="date-cell">
                <div>{{ formatDate(dispute.created_at) }}</div>
                <div v-if="dispute.resolved_at" class="resolved-date">{{ formatDate(dispute.resolved_at) }}</div>
              </div>
            </td>
            <td>
              <div class="action-group">
                <button 
                  @click="emit('edit', dispute)"
                  class="action-btn action-edit"
                  title="Edit dispute"
                >
                  <i class="fas fa-edit"></i>
                </button>
                <button 
                  @click="emit('add-document', dispute)"
                  class="action-btn action-document"
                  title="Add document"
                >
                  <i class="fas fa-file-alt"></i>
                </button>
                <button 
                  @click="emit('show-evidence', dispute)"
                  class="action-btn action-evidence"
                  :class="{ 'has-evidence': dispute.image_path || (dispute.documents && dispute.documents.length > 0) }"
                  title="View evidence"
                >
                  <i class="fas fa-image"></i>
                </button>
                <button 
                  v-if="dispute.status === 'open'"
                  @click="emit('resolve', dispute)"
                  class="action-btn action-resolve"
                  title="Resolve dispute"
                >
                  <i class="fas fa-check-circle"></i>
                </button>
                <button 
                  @click="emit('details', dispute)"
                  class="action-btn action-details"
                  title="View details"
                >
                  <i class="fas fa-info-circle"></i>
                </button>
                <button 
                  v-if="dispute.status === 'open'"
                  @click="emit('send-email', dispute)"
                  class="action-btn action-email"
                  title="Send email to finance company"
                >
                  <i class="fas fa-envelope"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DisputesTable',
  props: {
    disputes: { type: Array, required: true, default: () => [] },
    sortKey: { type: String, default: 'created_at' },
    sortAsc: { type: Boolean, default: false }
  },
  emits: ['sort', 'edit', 'add-document', 'show-evidence', 'resolve', 'details', 'send-email'],
  setup(props, { emit }) {
    const getSortIcon = (key) => {
      if (props.sortKey !== key) return '↕'
      return props.sortAsc ? '↑' : '↓'
    }
    
    const handleSort = (key) => {
      emit('sort', key)
    }
    
    const getCustomerName = (dispute) => dispute.customer.fullname || 'N/A'
    const getCustomerPhone = (dispute) => dispute.customer.phone || 'N/A'
    const getCustomerEmail = (dispute) => dispute.customer.email || 'N/A'
    
    const formatDisputeType = (type) => {
      const types = {
        'customer': 'Customer',
        'credit_card': 'Credit Card',
        'snap_finance': 'Finance',
        'merchant_service': 'Merchant Service',
        'attorney': 'Attorney',
        'court': 'Court'
      }
      return types[type] || type
    }
    
    const formatIssueType = (type) => {
      const types = {
        'fraud_charges': 'Fraud Charges',
        'broken_merchandise': 'Broken Merchandise',
        'wrong_item': 'Wrong Item',
        'missing_item': 'Missing Item',
        'not_as_described': 'Not as Described',
        'delivery_issue': 'Delivery Issue',
        'other': 'Other'
      }
      return types[type] || type
    }
    
    const formatResolution = (resolution) => {
      const resolutions = {
        'customer_favor': "Customer's Favor",
        'business_favor': "Business's Favor",
        'partial_refund': 'Partial Refund',
        'full_refund': 'Full Refund',
        'replacement': 'Replacement',
        'chargeback_won': 'Chargeback Won',
        'chargeback_lost': 'Chargeback Lost',
        'legal_action': 'Legal Action',
        'pending': 'Pending Resolution'
      }
      return resolutions[resolution] || resolution
    }
    
    const formatDate = (date) => {
      if (!date) return 'N/A'
      const d = new Date(date)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }
    
    const formatMoney = (amount) => {
      if (!amount) return '$0.00'
      return '$' + parseFloat(amount).toFixed(2)
    }
    
    const isCustomerFavor = (resolution) => {
      const customerFavorResolutions = ['customer_favor', 'partial_refund', 'full_refund', 'replacement', 'chargeback_lost']
      return customerFavorResolutions.includes(resolution)
    }
    
    const getResolutionOutcome = (resolution) => {
      if (!resolution || resolution === 'pending') return '—'
      if (isCustomerFavor(resolution)) return 'Customer Favor'
      return 'Business Favor'
    }
    
    return {
      getSortIcon,
      handleSort,
      getCustomerName,
      getCustomerPhone,
      getCustomerEmail,
      formatDisputeType,
      formatIssueType,
      formatResolution,
      formatDate,
      formatMoney,
      getResolutionOutcome,
      emit
    }
  }
}
</script>

<style scoped>
.disputed-orders__table-wrapper {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.table-container {
  overflow-x: auto;
}

.modern-table {
  width: 100%;
  min-width: 1300px;
  border-collapse: collapse;
  font-size: 14px;
}

/* Header */
.modern-table thead {
  background: #f9fafb;
}

.modern-table th {
  text-align: left;
  padding: 14px 16px;
  font-weight: 600;
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 2px solid #e5e7eb;
  white-space: nowrap;
}

.modern-table th:last-child {
  text-align: center;
}

/* Body */
.modern-table td {
  padding: 14px 16px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
  vertical-align: middle;
}

.modern-table tbody tr:hover {
  background: #f9fafb;
}

.modern-table tbody tr:last-child td {
  border-bottom: none;
}

/* Sortable */
.sortable {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
}

.sortable:hover {
  color: #111827;
}

.sort-indicator {
  display: inline-block;
  margin-left: 4px;
  font-size: 11px;
  color: #9ca3af;
}

/* Status Tags */
.status-tag {
  display: inline-block;
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: 600;
  margin-left: 8px;
  letter-spacing: 0.2px;
}

.status-tag.open {
  background: #fef3c7;
  color: #92400e;
}

.status-tag.resolved {
  background: #d1fae5;
  color: #065f46;
}

/* Customer */
.customer-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.customer-name {
  font-weight: 500;
  color: #111827;
}

.customer-detail {
  font-size: 12px;
  color: #9ca3af;
}

/* Invoice ID */
.invoice-id {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
}

/* Statement Status */
.statement-status {
  font-size: 13px;
  font-weight: 500;
}

.statement-status.received {
  color: #059669;
}

.statement-status.not-received {
  color: #dc2626;
}

/* Resolution */
.resolution-value {
  color: #059669;
  font-weight: 500;
}

.pending-value {
  color: #9ca3af;
}

/* Outcome */
.outcome-value {
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
}

.outcome-value.customer {
  background: #dbeafe;
  color: #1e40af;
}

.outcome-value.business {
  background: #fee2e2;
  color: #991b1b;
}

/* Money Lost */
.money-status {
  font-weight: 500;
}

.money-status.lost {
  color: #dc2626;
}

.money-status.none {
  color: #6b7280;
}

.amount-value {
  font-weight: 600;
  color: #111827;
}

.empty-value {
  color: #d1d5db;
}

/* Date */
.date-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
  color: #6b7280;
}

.resolved-date {
  font-size: 11px;
  color: #9ca3af;
}

/* Actions */
.actions-header {
  text-align: center !important;
  width:150px;

}

.action-group {
  display: flex;
  gap: 4px;
  justify-content: center;
  flex-wrap: wrap;
}

.action-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #6b7280;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #111827;
}

.action-btn i {
  font-size: 14px;
}

.action-resolve {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #059669;
}

.action-resolve:hover {
  background: #d1fae5;
  border-color: #6ee7b7;
}

.action-details {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #3b82f6;
}

.action-details:hover {
  background: #dbeafe;
  border-color: #93c5fd;
}

.action-edit {
  background: #fffbeb;
  border-color: #fde68a;
  color: #d97706;
}

.action-edit:hover {
  background: #fef3c7;
  border-color: #fcd34d;
}

.action-document {
  background: #f5f3ff;
  border-color: #c4b5fd;
  color: #7c3aed;
}

.action-document:hover {
  background: #ede9fe;
  border-color: #a78bfa;
}

.action-evidence {
  background: #fdf2f8;
  border-color: #f9a8d4;
  color: #ec4899;
}

.action-evidence.has-evidence {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #059669;
}

.action-evidence:hover {
  background: #fce7f3;
  border-color: #f472b6;
}

.action-evidence.has-evidence:hover {
  background: #d1fae5;
  border-color: #6ee7b7;
}

.action-email {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #2563eb;
}

.action-email:hover {
  background: #dbeafe;
  border-color: #93c5fd;
}

/* Responsive */
@media (max-width: 768px) {
  .modern-table {
    font-size: 13px;
  }
  
  .modern-table th,
  .modern-table td {
    padding: 10px 12px;
  }
  
  .action-group {
    gap: 3px;
  }
  
  .action-btn {
    width: 28px;
    height: 28px;
  }
  
  .action-btn i {
    font-size: 12px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/DisputedOrders/components/DisputesTable.vue